import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  Button,
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@porchpass/ui";
import { Upload } from "lucide-react";

interface ProfilePhotoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPhoto: string | null;
  onPhotoSelect: (photoUrl: string | null) => void;
  employeeName: string;
}

export default function ProfilePhotoDialog({
  open,
  onOpenChange,
  currentPhoto,
  onPhotoSelect,
  employeeName,
}: ProfilePhotoDialogProps) {
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(currentPhoto);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (file.type.startsWith("image/")) {
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewPhoto(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleRemovePhoto = () => {
    setPreviewPhoto(null);
  };

  const handleSave = () => {
    onPhotoSelect(previewPhoto);
  };

  const handleCancel = () => {
    setPreviewPhoto(currentPhoto);
    onOpenChange(false);
  };

  const getInitials = (name: string): string => {
    const parts = name.split(" ");
    return parts
      .slice(0, 2)
      .map((p) => p.charAt(0))
      .join("")
      .toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[430px]">
        <DialogHeader>
          <DialogTitle>Customize account photo</DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-6 items-center">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`relative w-full border-2 border-dashed rounded-[14px] p-6 text-center transition-colors ${
              isDragging
                ? "bg-blue-50 border-blue-500"
                : "border-transparent bg-[#f3f5f8] hover:border-grey-300"
            }`}
          >
            {previewPhoto ? (
              <div className="flex flex-col gap-6 items-center">
                <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                  <AvatarImage src={previewPhoto} alt="Preview" />
                  <AvatarFallback className="text-2xl font-semibold text-blue-600 bg-blue-100">
                    {getInitials(employeeName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-2 items-center">
                  <Button
                    variant="secondary"
                    size="s"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Select new photo
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-[#b93c3c] hover:text-[#b93c3c] hover:bg-[#b93c3c]/10"
                    onClick={handleRemovePhoto}
                  >
                    Remove current photo
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-6 items-center">
                <div className="flex justify-center items-center w-[38px] h-[38px] rounded-full bg-[#e5ebfd]">
                  <Upload className="w-[18px] h-[18px] text-primary" />
                </div>
                <p className="text-base font-semibold text-[#111827]">
                  Drop a photo here to upload.
                </p>
                <div className="flex flex-col gap-2 items-center">
                  <Button
                    variant="secondary"
                    size="s"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Select new photo
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-[#b93c3c] hover:text-[#b93c3c] hover:bg-[#b93c3c]/10"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Remove current photo
                  </Button>
                </div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          <p className="text-xs text-center text-[#4a5466] max-w-[276px]">
            Photos under 10MB of your face help customers and team members
            recognize you.
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="s" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="s" onClick={handleSave}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
